import type {
  VizSpec,
  LineBarGraphViz,
  BarChartViz,
  LineChartViz,
  KpiCardViz,
  PieDonutViz,
  ValueFormat
} from '../../api/ai-viz-types';
import type { NoDataViz } from '../../api/ai-viz-types';
import type { LabeledTableViz, LabeledVizSpec } from '../../api/ai-labeled-viz-types';

export type CubeRow = Record<string, unknown>;

// ─── Helpers (extracted from backend/src/ai/visualisations/types.ts) ───

export function toDayString(value: unknown): string {
  if (typeof value !== 'string') throw new Error('time key must be a string');

  // "YYYY-MM-DD"
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  // Cube-ish: "YYYY-MM-DD HH:MM:SS.ffffff"
  const m1 = value.match(/^(\d{4}-\d{2}-\d{2})\s/);
  if (m1) return m1[1];

  // ISO: "YYYY-MM-DDT..."
  const m2 = value.match(/^(\d{4}-\d{2}-\d{2})T/);
  if (m2) return m2[1];

  // last resort
  const dt = new Date(value);
  if (!Number.isNaN(dt.getTime())) return dt.toISOString().slice(0, 10);

  throw new Error(`Unrecognized day format: ${value}`);
}

export function toNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

// ─── Shared Formatter ───

export type CellFormat = ValueFormat | 'string';

// Cached Intl.NumberFormat instances — construction is ~10x slower than .format()
const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
const numberFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
const integerFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

export function formatVizValue(v: unknown, fmt: CellFormat): string {
  if (v === null || v === undefined) return '—';
  if (fmt === 'string') return String(v);
  if (fmt === 'enum') return formatEnumValue(String(v));

  if (fmt === 'date') {
    const d = new Date(String(v));
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);

  switch (fmt) {
    case 'currency':
      return currencyFmt.format(n);
    case 'percent':
      return `${(n * 100).toFixed(1)}%`;
    case 'number':
      return numberFmt.format(n);
    case 'integer':
      return integerFmt.format(Math.round(n));
  }
}

function formatEnumValue(s: string): string {
  return s
    .split('_')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

// ─── Raw Column Derivation (for "Show Data" tables) ───

export type RawColumn = { key: string; label: string; format: CellFormat };

function cubeKeyToLabel(key: string): string {
  const bare = key.includes('.') ? key.slice(key.indexOf('.') + 1) : key;
  return bare.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/;

function inferCellFormat(v: unknown): CellFormat {
  if (typeof v === 'number') return 'number';
  if (typeof v === 'string' && ISO_DATE_RE.test(v)) return 'date';
  return 'string';
}

const KNOWN_FORMATS = new Set<string>(['currency', 'percent', 'number', 'integer', 'date']);

export function deriveRawColumns(
  rows: CubeRow[],
  memberMeta?: Record<string, { title?: string; format?: string }>
): RawColumn[] {
  if (rows.length === 0) return [];
  const first = rows[0];
  return Object.keys(first).map(key => {
    const meta = memberMeta?.[key];
    const metaFormat = meta?.format && KNOWN_FORMATS.has(meta.format) ? meta.format : undefined;
    return {
      key,
      label: meta?.title ?? cubeKeyToLabel(key),
      format: (metaFormat as CellFormat) ?? inferCellFormat(first[key])
    };
  });
}

// ─── Transformers ───

export function transformLineBarGraph(
  rows: CubeRow[],
  viz: LineBarGraphViz
): Record<string, string | number | null>[] {
  const { timeKey, plannedKey, barKeys } = viz.mapping;

  return rows
    .map(r => {
      const row: Record<string, string | number | null> = {
        date: toDayString(r[timeKey]),
        [plannedKey]: toNumber(r[plannedKey])
      };
      for (const k of barKeys) {
        row[k] = toNumber(r[k]);
      }
      return row;
    })
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

export function transformBarChart(
  rows: CubeRow[],
  viz: BarChartViz
): Record<string, string | number | null>[] {
  const { categoryKey, valueKeys } = viz.mapping;

  return rows.map(r => {
    const row: Record<string, string | number | null> = {
      category: r[categoryKey] != null ? String(r[categoryKey]) : ''
    };
    for (const k of valueKeys) {
      row[k] = toNumber(r[k]);
    }
    return row;
  });
}

export function transformLineChart(
  rows: CubeRow[],
  viz: LineChartViz
): Record<string, string | number | null>[] {
  const { timeKey, lineKeys } = viz.mapping;

  return rows
    .map(r => {
      const row: Record<string, string | number | null> = {
        date: toDayString(r[timeKey])
      };
      for (const k of lineKeys) {
        row[k] = toNumber(r[k]);
      }
      return row;
    })
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

export type KpiCardData = {
  value: number | null;
  delta: number | null;
  label: string | null;
};

export function transformKpiCard(rows: CubeRow[], viz: KpiCardViz): KpiCardData {
  const row = rows[0] ?? {};
  return {
    value: toNumber(row[viz.mapping.valueKey]),
    delta: viz.mapping.deltaKey ? toNumber(row[viz.mapping.deltaKey]) : null,
    label: null
  };
}

export type PieDonutData = {
  name: string;
  value: number | null;
};

export function transformPieDonut(rows: CubeRow[], viz: PieDonutViz): PieDonutData[] {
  const { categoryKey, valueKey } = viz.mapping;

  return rows.map(r => ({
    name: r[categoryKey] != null ? String(r[categoryKey]) : '',
    value: toNumber(r[valueKey])
  }));
}

export type TableData = {
  columns: { key: string; label: string; format?: CellFormat }[];
  data: Record<string, unknown>[];
};

export function transformTable(rows: CubeRow[], viz: LabeledTableViz): TableData {
  return {
    columns: viz.columns.map(c => ({
      key: c.key,
      label: c.label,
      format: c.format
    })),
    data: rows.map(r => {
      const row: Record<string, unknown> = {};
      for (const col of viz.columns) {
        row[col.key] = r[col.key] ?? null;
      }
      return row;
    })
  };
}

// ─── Dispatcher ───

export type TransformedVizData =
  | { vizType: 'line_bar_graph'; data: Record<string, string | number | null>[] }
  | { vizType: 'bar_chart'; data: Record<string, string | number | null>[] }
  | { vizType: 'line_chart'; data: Record<string, string | number | null>[] }
  | { vizType: 'kpi_card'; data: KpiCardData }
  | { vizType: 'pie_donut'; data: PieDonutData[] }
  | { vizType: 'table'; data: TableData }
  | null;

export function transformVizData(
  rows: CubeRow[],
  viz: LabeledVizSpec | NoDataViz
): TransformedVizData {
  switch (viz.vizType) {
    case 'line_bar_graph':
      return { vizType: 'line_bar_graph', data: transformLineBarGraph(rows, viz) };
    case 'bar_chart':
      return { vizType: 'bar_chart', data: transformBarChart(rows, viz) };
    case 'line_chart':
      return { vizType: 'line_chart', data: transformLineChart(rows, viz) };
    case 'kpi_card':
      return { vizType: 'kpi_card', data: transformKpiCard(rows, viz) };
    case 'pie_donut':
      return { vizType: 'pie_donut', data: transformPieDonut(rows, viz) };
    case 'table':
      return { vizType: 'table', data: transformTable(rows, viz) };
    case 'no_data':
      return null;
  }
}

// ─── Validation ───

export function validateVizMapping(
  viz: VizSpec,
  rows: CubeRow[]
): { valid: true } | { valid: false; missing: string[] } {
  if (viz.vizType === 'no_data') return { valid: true };
  if (rows.length === 0) return { valid: true };

  const row = rows[0];
  const availableKeys = new Set(Object.keys(row));
  const requiredKeys: string[] = [];

  switch (viz.vizType) {
    case 'line_bar_graph':
      requiredKeys.push(viz.mapping.timeKey, viz.mapping.plannedKey, ...viz.mapping.barKeys);
      break;
    case 'bar_chart':
      requiredKeys.push(viz.mapping.categoryKey, ...viz.mapping.valueKeys);
      break;
    case 'line_chart':
      requiredKeys.push(viz.mapping.timeKey, ...viz.mapping.lineKeys);
      break;
    case 'kpi_card':
      requiredKeys.push(viz.mapping.valueKey);
      if (viz.mapping.deltaKey) requiredKeys.push(viz.mapping.deltaKey);
      break;
    case 'pie_donut':
      requiredKeys.push(viz.mapping.categoryKey, viz.mapping.valueKey);
      break;
    case 'table':
      requiredKeys.push(...viz.columns.map(c => c.key));
      break;
  }

  const missing = requiredKeys.filter(k => !availableKeys.has(k));
  return missing.length === 0 ? { valid: true } : { valid: false, missing };
}
